// 🎥 VideoSection - Galerie vidéo avec lecteur YouTube intégré
// Affiche les vidéos du festival avec sélection et miniatures
// Utilisé dans : src/pages/HomePage.jsx

import { Play, ExternalLink } from 'lucide-react'
import { useState } from 'react'

// Styles CSS pour masquer la scrollbar tout en gardant le scroll horizontal
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`

/**
 * Composant de galerie vidéo avec lecteur intégré
 * Permet de naviguer entre plusieurs vidéos YouTube
 * 
 * @param {Object} props - Propriétés du composant
 * @param {Array} props.videos - Liste des vidéos [{ id, title, thumbnail, youtubeId }]
 */
export default function VideoSection({ videos }) {
  // État pour la vidéo sélectionnée dans le lecteur
  const [selectedVideo, setSelectedVideo] = useState(videos[0]?.id || null)

  // Early return si aucune vidéo
  if (!videos || videos.length === 0) return null

  // Vidéo actuellement sélectionnée pour le lecteur
  const currentVideo = videos.find(v => v.id === selectedVideo) || videos[0]
  const allVideos = videos // Toutes les vidéos pour les miniatures

  return (
    <>
      <style>{scrollbarHideStyles}</style>
      <section className="section-padding bg-neutral-50 dark:bg-neutral-900">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-950 text-primary-600 dark:text-primary-400 rounded-full text-sm font-medium mb-4">
            🎥 Vidéos
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-neutral-900 dark:text-neutral-100 mb-4">
            Revivez l'ambiance Rock'In Agde
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
            Découvrez les moments forts, les coulisses et l'énergie de nos éditions passées
          </p>
        </div>

        {/* Main Video Player - Mockup Tablette */}
        <div className="max-w-5xl mx-auto mb-8">
          {/* Cadre tablette/device mockup */}
          <div className="relative mx-auto" style={{ maxWidth: '900px' }}>
            {/* Cadre extérieur tablette */}
            <div className="relative bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-2xl md:rounded-3xl p-2 md:p-4 shadow-2xl">
              {/* Écran */}
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
            {currentVideo.type === 'youtube' ? (
              <iframe
                src={`https://www.youtube.com/embed/${currentVideo.videoId}?autoplay=0&mute=1`}
                title={currentVideo.title}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <>
                <img
                  src={currentVideo.thumbnail}
                  alt={currentVideo.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSelectedVideo(currentVideo.id)}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors"
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                    <Play size={32} className="text-primary-600 ml-1" fill="currentColor" />
                  </div>
                </button>
              </>
            )}
            
                {/* Title overlay - n'empêche pas l'accès aux contrôles */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-3 md:p-6 pointer-events-none">
                  <h3 className="text-white font-bold text-sm md:text-xl lg:text-2xl mb-1 md:mb-2">
                    {currentVideo.title}
                  </h3>
                  {currentVideo.description && (
                    <p className="hidden md:block text-neutral-200 text-sm">
                      {currentVideo.description}
                    </p>
                  )}
                </div>
              </div>
              {/* Bouton home/point de la tablette */}
              <div className="hidden md:block absolute bottom-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-neutral-600 rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Videos Grid */}
        {allVideos.length > 0 && (
          <div className="max-w-5xl mx-auto">
            <div className="flex gap-6 overflow-x-auto py-4 scrollbar-hide justify-center">
              {allVideos.map((video) => (
                <div
                  key={video.id}
                  className="group cursor-pointer flex-shrink-0 transition-transform hover:scale-105"
                  style={{ width: '220px' }}
                  onClick={() => setSelectedVideo(video.id)}
                >
                  {/* Image container avec aspect ratio fixe */}
                  <div className={`relative w-full rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all bg-black ${
                    video.id === selectedVideo 
                      ? 'ring-4 ring-primary-500 shadow-2xl' 
                      : ''
                  }`} style={{ aspectRatio: '16/9' }}>
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 flex items-center justify-center transition-colors">
                      <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play size={20} className="text-primary-600 ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Titre en dessous de l'image */}
                  <div className="mt-3">
                    <h4 className="text-neutral-900 dark:text-neutral-100 font-semibold text-sm leading-tight line-clamp-2">
                      {video.title}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
    </>
  )
}
